import { PaginatedResponse, useApi } from '@/api';
import { Link, TableCellBottom } from '@/components';
import { DEFAULT_NETWORK } from '@/constants';
import {
  CurrencyValue,
  DataTable,
  RelativeDateTime,
  TableSkeleton,
  useTableProps,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useParams } from 'react-router';

type LiquidatorsData = {
  liquidator_address: string;
  last_active: string;
  liquidations: string | number | null;
  total_collateral: string;
  total_debt: string;
  total_profits: string | null;
  share: string;
  order_index: string;
};

export default function LiquidatorTable() {
  const { liquidatorAddress } = useParams() as {
    liquidatorAddress: string;
  };
  const network = DEFAULT_NETWORK;
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-datetime',
    filtersDebounceTime: 500
  });
  const [data, error] = useApi<PaginatedResponse<LiquidatorsData[]>>({
    url: `/liquidations/liquidators/${liquidatorAddress}/liquidations/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: 'Wallet Address',
      getCell: (row) => (
        <Link
          key={row.wallet_address}
          to={`/wallets/${row.wallet_address}`}
          relative="path">
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Collateral Liquidated',
      getCell: (row) => (
        <CurrencyValue
          value={row.collateral_seized}
          options={{
            currencySize: '-1',
            currency: {
              name: row.collateral_underlying_symbol,
              address: row.collateral_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.collateral_seized_usd}>
          <CurrencyValue
            value={row.collateral_seized_usd}
            options={{
              variant: 'muted',
              currency: 'USD'
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'collateral_seized_usd'
    },
    {
      header: 'Debt Repaid',
      getCell: (row) => (
        <CurrencyValue
          value={row.debt_repaid}
          options={{
            currencySize: '-1',
            currency: {
              name: row.debt_underlying_symbol,
              address: row.debt_underlying_address,
              network: network
            }
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.debt_repaid_usd}>
          <CurrencyValue
            value={row.debt_repaid_usd}
            options={{
              currency: 'USD'
            }}
            variant="muted"
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'debt_repaid_usd'
    },
    {
      header: 'Date',
      getCell: (row) => <RelativeDateTime value={row.datetime} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];

  return (
    <DataTable
      rows={data['results']}
      columns={columns}
      getKey={(row) => `${row.order_index}`}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.pagination.total
      }}
    />
  );
}
